/**
 * Android Back Button Handler Hook
 *
 * Gestisce il back button hardware di Android:
 * - Se c'è un handler locale (modal aperto) → chiudi il modal
 * - Se sei in una pagina interna → torna indietro con navigate(-1)
 * - Se sei nella home → richiedi doppio tap per uscire
 *
 * NOTE: In una Capacitor SPA, `canGoBack` da Capacitor è SEMPRE false
 * perché il WebView non ha history browser. Usiamo il nostro stack.
 */

import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { logger } from '@deskbay/shared';

// ---------------------------------------------------------------------------
// Registry globale degli handler locali (es. modal, wizard step)
// ---------------------------------------------------------------------------
export type BackHandler = () => boolean | void;
const customBackHandlers: BackHandler[] = [];

export function registerBackHandler(handler: BackHandler) {
  customBackHandlers.push(handler);
  return () => {
    const idx = customBackHandlers.indexOf(handler);
    if (idx > -1) customBackHandlers.splice(idx, 1);
  };
}

// ---------------------------------------------------------------------------
// Rotte
// ---------------------------------------------------------------------------
// Rotte di "home" — pressione back qui attiva il dialogo di uscita
const HOME_ROUTES = ['/dashboard', '/'];

// Questi path non vengono pushed nello stack (sono redirect o tab-swap)
const SKIP_STACK_ROUTES = [
  '/dashboard/bookings',
  '/dashboard/subscription',
  '/dashboard/settings',
  '/dashboard/stats',
];

// ---------------------------------------------------------------------------
// Stato globale del listener (singleton)
// ---------------------------------------------------------------------------
let globalListenerHandle: { remove: () => void } | null = null;
let isListenerSetup = false;

// Stack minimo per capire se siamo "nella home" o in una pagina interna.
// Non viene usato per navigate (usiamo navigate(-1)), solo per rilevare
// se siamo al root dell'app.
const navigationStack: string[] = [];

// ---------------------------------------------------------------------------
export function useAndroidBackButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const lastBackPress = useRef<number>(0);

  // ref aggiornati ad ogni render per avere sempre i valori correnti nella closure del listener
  const navigateRef = useRef(navigate);
  const locationRef = useRef(location);

  useEffect(() => {
    navigateRef.current = navigate;
    locationRef.current = location;
  }, [navigate, location]);

  // Traccia lo stack di navigazione (ignora route di redirect)
  useEffect(() => {
    const path = location.pathname;
    if (SKIP_STACK_ROUTES.includes(path)) return;

    const last = navigationStack[navigationStack.length - 1];
    if (last !== path) {
      navigationStack.push(path);
      logger.capacitor.debug('Nav stack push: ' + path, 'useAndroidBackButton');
    }
  }, [location.pathname]);

  // Registra il listener Capacitor (una sola volta, globale)
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
      return;
    }

    if (isListenerSetup) return;
    isListenerSetup = true;

    const setupListener = async () => {
      const { App: CapacitorApp } = await import('@capacitor/app');

      // NON usiamo più removeAllListeners perché potrebbe rompere hook interni di Capacitor
      // Lo sovrascriviamo semplicemente aggiungendo il nostro listener che gestisce tutto.

      if (globalListenerHandle) {
        await globalListenerHandle.remove();
      }

      globalListenerHandle = await CapacitorApp.addListener('backButton', () => {
        // 0. Chiudi tastiera se aperta
        if (document.activeElement && 'blur' in document.activeElement) {
          (document.activeElement as HTMLElement).blur();
        }

        // 1. Handler locale (modal, step wizard, ecc.)
        if (customBackHandlers.length > 0) {
          const topHandler = customBackHandlers[customBackHandlers.length - 1];
          const handled = topHandler();
          if (handled !== false) {
            logger.capacitor.debug(
              'Back intercepted by local handler',
              'useAndroidBackButton'
            );
            return;
          }
        }

        // 2. Routing globale — leggi percorso corrente DAL REF (sicuro nella closure)
        const currentPath = locationRef.current.pathname;
        const isHome = HOME_ROUTES.some(r => currentPath === r || currentPath === r + '/');
        const canPopStack = navigationStack.length > 1;

        logger.capacitor.debug(`Hw Back pressed at ${currentPath} (isHome: ${isHome}, canPop: ${canPopStack})`, 'useAndroidBackButton');

        if (!isHome && canPopStack) {
          // Pagina interna: torna indietro tramite React Router
          navigationStack.pop();
          logger.capacitor.debug('Navigating back via router', 'useAndroidBackButton');
          navigateRef.current(-1);
        } else {
          // Home o stack vuoto: doppio tap per uscire se siamo in una home route
          if (isHome) {
            const now = Date.now();
            if (now - lastBackPress.current < 2000) {
              logger.capacitor.info('Exiting app', 'useAndroidBackButton');
              CapacitorApp.exitApp();
            } else {
              lastBackPress.current = now;
              logger.capacitor.debug('Home detected: double tap to exit', 'useAndroidBackButton');
              if (typeof (window as any).showToast === 'function') {
                (window as any).showToast('Premi ancora per uscire');
              }
            }
          } else {
            // Se non siamo in Home ma lo stack è vuoto, torniamo in dashboard come fallback
            logger.capacitor.debug('Stack empty but not home, falling back to dashboard', 'useAndroidBackButton');
            navigateRef.current('/dashboard');
          }
        }
      });

      logger.capacitor.info(
        'Android back button listener registered (refined)',
        'useAndroidBackButton'
      );
    };

    setupListener();

    // Il listener è globale: non fare cleanup sul cleanup del componente
    return undefined;
  }, []);
}
